import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { TimeTrackingProvider } from './contexts/TimeTrackingContext';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Lazy load all route components for code splitting with automatic retry
// This reduces initial bundle size and handles chunk load failures gracefully
const LandingPage = lazyWithRetry(() => import('./components/LandingPage'));
const FreeTrialApp = lazyWithRetry(() => import('./components/FreeTrialAppRefactored'));
const AdminPanel = lazyWithRetry(() => import('./components/AdminPanel'));
const Pricing = lazyWithRetry(() => import('./components/Pricing'));
const PaymentSuccess = lazyWithRetry(() => import('./components/PaymentSuccess'));
const PaymentError = lazyWithRetry(() => import('./components/PaymentError'));
const TermsOfService = lazyWithRetry(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/PrivacyPolicy'));
const RefundPolicy = lazyWithRetry(() => import('./components/RefundPolicy'));
const ComingSoon = lazyWithRetry(() => import('./components/ComingSoon'));
const NotFound = lazyWithRetry(() => import('./components/NotFound'));

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

/**
 * ResetPage - Emergency recovery route
 * 
 * Clears all local storage and redirects to homepage.
 * Use when users are stuck in a broken state.
 * Access via: /reset
 */
const ResetPage = () => {
  useEffect(() => {
    // Clear all local storage
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('[ResetPage] Cleared all local storage');
    } catch (e) {
      console.error('[ResetPage] Failed to clear storage:', e);
    }

    // Redirect to home after brief delay
    const timer = setTimeout(() => {
      window.location.replace('/');
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
      <div className="text-center p-8">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Resetting Application</h1>
        <p className="text-gray-600">Clearing cached data and redirecting...</p>
        <p className="text-sm text-gray-400 mt-4">You will be redirected to the homepage shortly.</p>
      </div>
    </div>
  );
};

const AppRouter = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Public pricing page */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />

        {/* Payment success page */}
        <Route path="/payment/success" element={<PaymentSuccess />} />

        {/* Payment error page */}
        <Route path="/payment/error" element={<PaymentError />} />

        {/* Coming Soon page for Mastery/Ultimate plans */}
        <Route path="/coming-soon" element={<ComingSoon />} />

        {/* Emergency reset route - clears local state */}
        <Route path="/reset" element={<ResetPage />} />

        {/* Protected admin panel - ONLY ethangrucza@gmail.com */}
        <Route
          path="/app/admin"
          element={
            isAuthenticated ? <AdminPanel /> : <Navigate to="/" replace />
          }
        />

        {/* Protected main app - Free Trial (handles /app, /app/calendar, /app/membership, etc.) */}
        <Route
          path="/app/*"
          element={
            isAuthenticated ? (
              <TimeTrackingProvider userId={user?.sub}>
                <FreeTrialApp />
              </TimeTrackingProvider>
            ) : <Navigate to="/" replace />
          }
        />

        {/* 404 Not Found - catch all unknown routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
